// Linear combat layout: two facing columns, one per team.
// Landscape: player on left half, enemy on right half, both at the same Y.
// Portrait:  player on bottom row, enemy on top row, both spread across full width.
// Slot 0 = frontline (nearest to combat centre), slot 2 = backline.
// HexCoord {q: slot (0-2), r: 0=player / 1=enemy} for VFX compatibility.

import type { HexCoord } from "./hex/HexCoord";

export interface Point {
  x: number;
  y: number;
}

export const SLOT_COUNT = 3;

export class SlotLayout {
  size: number;
  readonly portrait: boolean;
  readonly playerY: number;
  readonly enemyY: number;

  /**
   * @param w         Canvas width in CSS px
   * @param playerY   Y centre for the player team (= combatY in landscape)
   * @param unitSize  Diameter hint — size = unitSize / 2
   * @param portrait  When true, units spread across full width at separate Y rows
   * @param enemyY    Y centre for the enemy team (portrait only; defaults to playerY)
   */
  constructor(
    public w: number,
    playerY: number,
    unitSize = 44,
    portrait = false,
    enemyY?: number,
  ) {
    this.size = unitSize / 2;
    this.portrait = portrait;
    this.playerY = playerY;
    this.enemyY = portrait ? (enemyY ?? playerY) : playerY;
  }

  /** Midpoint between teams — used for VFX and divider placement. */
  get combatY(): number {
    return (this.playerY + this.enemyY) / 2;
  }

  slotToPixel(slot: number, team: "player" | "enemy"): Point {
    if (this.portrait) {
      // Full-width spread; slot 0 at left, slot 4 at right.
      const xSpacing = this.w / (SLOT_COUNT + 1);
      return {
        x: xSpacing * (slot + 1),
        y: team === "player" ? this.playerY : this.enemyY,
      };
    }
    // Landscape: player occupies left half, enemy occupies right half.
    const halfW = this.w / 2;
    const xSpacing = halfW / (SLOT_COUNT + 1);
    const distFromCentre = xSpacing * (slot + 1);
    return {
      x: team === "player" ? halfW - distFromCentre : halfW + distFromCentre,
      y: this.playerY,
    };
  }

  hexToPixel(coord: HexCoord): Point {
    return this.slotToPixel(coord.q, coord.r === 0 ? "player" : "enemy");
  }

  rankToPixel(rank: number, _total: number, team: "player" | "enemy"): Point {
    return this.slotToPixel(rank, team);
  }
}
